import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";

import QuizActivity from "../../../Shared/Activities/QuizActivity";
import SentenceOrdering from "../../../Shared/Activities/SentenceOrdering";
import VocabMatch from "../../../Shared/Activities/VocabMatch";
import GapFillActivity from "../../../Shared/Activities/GapFillExercise";
import GrammarExerciseComp from "../../../Shared/Activities/GrammarExerciseComp.jsx";
import ReadingActivity from "../../../Shared/Activities/ReadingActivity.jsx";
import Videos from "../../../Shared/Activities/VideoActivity.jsx";
import shuffle from "../../../Shared/shuffle.jsx";

import "./exercise-section.css";

const ExerciseSection = ({ type, data, id, scrollTargetRef }) => {
  const topRef = useRef(null);

  const [activityStarted, setActivityStarted] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [activityEnded, setActivityEnded] = useState(false);

  // 🚨 This was the problem: Vocab should NOT start as array
  const [activityData, setActivityData] = useState(data);

  const [usedIds, setUsedIds] = useState([]);
  const [selectedTopic, setSelectedTopic] = useState("All Topics");

  const autoStartTypes = useMemo(
    () => ["ordering", "vocab", "gapfill", "grammar", "reading", "video"],
    []
  );

  const getUniqueTopics = useMemo(() => {
    if (type !== "quiz" || !data?.questions) return ["All Topics"];
    const topics = data.questions
      .map(q => q.topic)
      .filter(Boolean);
    return ["All Topics", ...Array.from(new Set(topics))];
  }, [type, data]);

  const scrollToTop = () => {
    const target = scrollTargetRef?.current;

    if (target) {
      const rect = target.getBoundingClientRect();
      const top = rect.top + window.scrollY - 60;
      window.scrollTo({ top, behavior: "smooth" });
    } else {
      topRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  };

  const getTitle = () => {
    if (type === "quiz") return data?.quizTitle ?? "Quiz";
    if (type === "ordering") return data?.title ?? "Sentence Ordering";
    if (type === "vocab") return data?.quizTitle ?? "Vocabulary Match";
    if (type === "gapfill") return data?.title ?? "Gap Fill";
    if (type === "grammar") return data?.title ?? "Grammar Exercise";
    if (type === "reading") return data?.title ?? "Reading Comprehension";
    if (type === "video") return data?.title ?? "Videos";
    return "Activity";
  };

  const getRawData = useCallback(() => {
    if (type === "quiz") return Array.isArray(data?.questions) ? data.questions : [];
    if (type === "ordering") return Array.isArray(data) ? data : [];
    if (type === "vocab") return data?.cards ?? [];
    if (type === "gapfill") return Array.isArray(data?.text) ? [data] : [];
    if (type === "grammar") return Array.isArray(data?.sentences) ? data.sentences : [];
    if (type === "reading") return Array.isArray(data) ? data : [];
    if (type === "video") return Array.isArray(data) ? data : [];
    return [];
  }, [type, data]);

  const isSingleVideo = type === "video" && id !== undefined;

  /** -------------------------------------
   * AUTO-START LOADING
   --------------------------------------*/
  useEffect(() => {
    if (type === "video") {
      const normalized = Array.isArray(data) ? data : [data];
      setActivityData(normalized);
      setActivityStarted(true);
    } else if (autoStartTypes.includes(type)) {
      // 🚨 For vocab, keep full object, not just pairs
      if (type === "vocab") {
        setActivityData(data);
        setActivityStarted(true);
      } else {
        setActivityData(getRawData());
        setActivityStarted(true);
      }
    }
  }, [type, data, autoStartTypes, getRawData]);

  /** -------------------------------------
   * START BUTTON HANDLER
   --------------------------------------*/
  const startActivity = () => {
    const raw = getRawData();

    if (type === "vocab") {
      // ❗ DO NOT SHUFFLE — return full object
      setActivityData({
        ...data,
        cards: raw
      });
    } else if (type === "gapfill") {
      setActivityData(shuffle([...raw]));
    } else if (type === "quiz") {
      let unused = raw.filter((item) => !usedIds.includes(item.id));
      
      // Apply topic filter if not "All Topics"
      if (selectedTopic !== "All Topics") {
        unused = unused.filter(q => q.topic === selectedTopic);
      }
      
      const toUse = unused.slice(0, 10);
      setUsedIds((prev) => [...prev, ...toUse.map((i) => i.id)]);
      setActivityData(toUse);
    } else {
      const unused = raw.filter((item) => !usedIds.includes(item.id));
      const toUse = shuffle(unused).slice(0, 10);
      setUsedIds((prev) => [...prev, ...toUse.map((i) => i.id)]);
      setActivityData(toUse);
    }

    setActivityStarted(true);
    setActivityEnded(false);
    setCurrentIndex(0);
    setScore(0);
    scrollToTop();
  };

  /** -------------------------------------
   * ACTIVITY COMPONENT RENDERER
   --------------------------------------*/
  const ActivityComponent = () => {
    const current = Array.isArray(activityData)
      ? activityData[currentIndex]
      : activityData;

    if (!current) return <p>No data</p>;

    return (
      <div className="exercisesection__activity-box">
        {type === "quiz" && (
          <QuizActivity
            key={currentIndex}
            question={current}
            questionIndex={currentIndex}          // ← pass the current index
            totalQuestions={activityData.length} // ← filtered count
            onAnswer={(result) => {
              if (result.correct) setScore((s) => s + 1);
              if (result.next) setCurrentIndex((i) => i + 1);
            }}
          />
        )}

        {type === "ordering" && (
          <SentenceOrdering
            data={current}
            onCorrect={() => setScore((s) => s + 1)}
          />
        )}

        {type === "vocab" && (
          <>
            {console.log("VocabMatch data SENT TO COMPONENT:", activityData)}
            <VocabMatch
              data={activityData}
              onCorrect={() => setScore((s) => s + 1)}
            />
          </>
        )}

        {type === "gapfill" && (
          <GapFillActivity
            data={current}
            onComplete={() => setScore((s) => s + 1)}
          />
        )}

        {type === "grammar" && (
          <GrammarExerciseComp
            data={getRawData()}
            title={data?.title}
            description={data?.description}
          />
        )}

        {type === "reading" && (
          <ReadingActivity
            data={activityData[currentIndex]}
            title={activityData[currentIndex].readingcompTitle}
          />
        )}

        {type === "video" && (
          <Videos
            data={activityData[currentIndex]}
            title={activityData[currentIndex].title}
          />
        )}
      </div>
    );
  };

  /** -------------------------------------
   * MAIN JSX
   --------------------------------------*/
  return (
    <div className="exercisesection">
      <div ref={topRef} />

      {!activityStarted && !autoStartTypes.includes(type) ? (
        <div className="exercisesection__start">
          <div className="exercisesection__quiz-card">
            <span className="exercisesection__title">{getTitle()}</span>
            {type === "quiz" && getUniqueTopics.length > 1 && (
              <select 
                value={selectedTopic}
                onChange={(e) => setSelectedTopic(e.target.value)}
                className="exercisesection__topic-filter"
              >
                {getUniqueTopics.map(topic => (
                  <option key={topic} value={topic}>{topic}</option>
                ))}
              </select>
            )}
            <button onClick={startActivity} className="exercisesection__start-button">
              Start
            </button>
          </div>
        </div>
      ) : activityEnded ? (
        <div className="exercisesection__score-card">
          <h2>Activity Completed!</h2>
          <p>
            Your score: {score}
          </p>

          <div className="newexerciseblock">
            <button onClick={() => setActivityStarted(false)} className="exercisesection__restart-button">
              Restart
            </button>
          </div>
        </div>
      ) : (
        <>
          {isSingleVideo ? (
            <Videos data={data} title={data.title} />
          ) : (
            <>
              <ActivityComponent />

              {activityStarted && (
                <div className="exercisesection__nav button-row">
                  <button
                    onClick={() => setActivityStarted(false)}
                    className="exercisesection__nav-button"
                  >
                    Reset
                  </button>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default ExerciseSection;
